// An environment's VPN for its owner (D80): the state, Connect (the relay command and the
// sign-in link), Disconnect, and the profile and DNS. While a sign-in waits the state is read
// every 3 s, so "Connected" appears as soon as the relayed callback lands.
import { ShieldCheck } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "rizzui";

import { useT } from "../i18n";
import { cardClass } from "../ui/Card";
import { cn } from "../ui/cn";
import { ErrorState, Loading } from "../ui/states";
import { connectVpn, disconnectVpn, environmentVpnQuery, saveVpn, type VpnLogin, type VpnSettingsBody, vpnState } from "./api";
import { ConnectSteps } from "./ConnectSteps";
import { VpnSettingsForm } from "./VpnSettingsForm";
import { VpnStatusPill } from "./VpnStatus";

const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function VpnPanel({ environmentId, orgId, className }: { environmentId: string; orgId: string; className?: string }) {
  const t = useT().vpn;
  const client = useQueryClient();
  const [login, setLogin] = useState<VpnLogin | null>(null);
  const waiting = login !== null;
  const vpn = useQuery(environmentVpnQuery(environmentId, waiting ? 3_000 : 15_000));
  const refresh = () => client.invalidateQueries({ queryKey: ["environment-vpn", environmentId] });
  const save = useMutation({ mutationFn: (body: VpnSettingsBody) => saveVpn(environmentId, body), onSuccess: refresh });
  const connect = useMutation({ mutationFn: () => connectVpn(environmentId), onSuccess: (l) => setLogin(l) });
  const disconnect = useMutation({
    mutationFn: () => disconnectVpn(environmentId),
    onSuccess: () => {
      setLogin(null);
      void refresh();
    },
  });
  if (vpn.isPending) return <Loading />;
  if (vpn.isError) return <ErrorState title={t.loadFailed} message={vpn.error.message} />;
  const state = vpnState(vpn.data);
  const shownLogin = state === "up" ? null : login;
  const failure = connect.error ?? disconnect.error ?? save.error;
  return (
    <section className={cn(cardClass, "space-y-5 p-5", className)} aria-label={t.title}>
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="m-0 flex items-center gap-2 text-base font-semibold text-gray-900">
          <ShieldCheck weight="duotone" className="h-5 w-5 text-primary" /> {t.title}
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          <VpnStatusPill state={state} />
          {vpn.data.settings && state !== "up" ? (
            <Button size="sm" isLoading={connect.isPending} onClick={() => connect.mutate()}>
              {login ? t.connect.again : t.connect.connect}
            </Button>
          ) : null}
          {state === "up" ? (
            <Button size="sm" variant="outline" isLoading={disconnect.isPending} onClick={() => disconnect.mutate()}>
              {t.connect.disconnect}
            </Button>
          ) : null}
        </div>
      </header>
      {state === "up" && login ? <p className="m-0 text-sm text-green-dark">{t.connect.connected}</p> : null}
      {vpn.data.last_error ? <p className="m-0 text-sm text-red-dark">{t.connect.failed(vpn.data.last_error)}</p> : null}
      {failure ? <p role="alert" className="m-0 text-sm text-red-dark">{message(failure)}</p> : null}
      {shownLogin ? (
        <div className="space-y-3 rounded-lg border border-muted p-4">
          <ConnectSteps login={shownLogin} origin={window.location.origin} />
          <p className="m-0 text-[13px] text-gray-500" aria-live="polite">{t.connect.waiting}</p>
        </div>
      ) : null}
      <details open={!vpn.data.settings}>
        <summary className="cursor-pointer text-sm font-medium text-gray-900">{t.settings.title}</summary>
        <div className="mt-3">
          <VpnSettingsForm key={JSON.stringify(vpn.data.settings)} orgId={orgId} vpn={vpn.data} saving={save.isPending} onSave={(b) => save.mutate(b)} />
        </div>
      </details>
    </section>
  );
}
